import {useCallback, useEffect, useMemo, useState} from 'react';
import {MailWarning} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldLabel,
  Input,
  ScrollArea,
  Spinner
} from '@kroll-resolver-crisp/ui-components-react';
import {
  EscalationVariantPicker,
  getPolicyViolationsMissingVariant
} from './EscalationVariantPicker';
import {useAuth} from '@/context/useAuth';
import {useCreateP0Alert, useEscalationVariants} from '@/api/hooks';
import {getApiErrorMessage} from '@/api/client';
import {RichTextArea, isRichTextEmpty, EmailPreview} from '@/components/shared/RichTextArea';
import {showToast} from '@/lib/toast';
import {ACTOR_LABEL, CONTENT_LABEL, PLATFORM_ID} from '@/lib/platform';

/// The minimal shape CreateP0AlertDialog needs to display and template a linked item - built either
/// straight from an already-classified `Submission` (Submissions page) or from a freshly-classified
/// `Item` plus the policy violation/submission type just chosen for it (Investigations, the mod queue).
export type P0LinkedItem = {
  id: string;
  submissionType: 'Actor' | 'Content';
  policyViolation: string;
  displayName: string;
  externalId: string;
  /// The platform username/handle, when known - listed beside the id on Falcon's alert.
  handle?: string | null;
};

type Props = {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /// The actual submission stream ids to reference - what the backend expects as submission_ids.
  readonly submissionIds: string[];
  /// Display + templating data for each linked item, in the same order as submissionIds ideally but
  /// not required to correspond 1:1 by index (matched only by count for display purposes).
  readonly linkedItems: P0LinkedItem[];
  readonly onCreated?: () => void;
};

/// Open/linked-items state for a `CreateP0AlertDialog` instance, shared across every entry point
/// (Submissions, Investigations, the mod queue, `/submission`) so each one isn't hand-rolling the same
/// open flag + item list + prop wiring. `launch` covers the common "snapshot the items, then open"
/// case (e.g. right after a classify action); a caller whose linked items are instead derived live
/// from other state (e.g. Submissions' current row selection) can ignore `linkedItems`/`launch` and
/// just call `setOpen(true)`, passing its own array into `dialogProps`.
export function useP0AlertDialog() {
  const [open, setOpen] = useState(false);
  const [linkedItems, setLinkedItems] = useState<P0LinkedItem[]>([]);

  const launch = useCallback((items: P0LinkedItem[]) => {
    setLinkedItems(items);
    setOpen(true);
  }, []);

  const dialogProps = useCallback(
    (items: P0LinkedItem[] = linkedItems, extra?: Pick<Props, 'onCreated'>) => ({
      open,
      submissionIds: items.map((i) => i.id),
      linkedItems: items,
      onOpenChange: setOpen,
      ...extra
    }),
    [open, linkedItems]
  );

  return useMemo(
    () => ({open, setOpen, linkedItems, setLinkedItems, launch, dialogProps}),
    [open, linkedItems, launch, dialogProps]
  );
}

/// Same P0 alert creation flow regardless of where it's triggered from: Submissions (already-classified
/// items), or Investigations/the mod queue (classify-then-immediately-P0). Always creates exactly one
/// P0 alert referencing every id in submissionIds.
export function CreateP0AlertDialog({
  open,
  onOpenChange,
  submissionIds,
  linkedItems,
  onCreated
}: Props) {
  const {user} = useAuth();
  const createP0Alert = useCreateP0Alert();

  const [subject, setSubject] = useState('');
  const [notes, setNotes] = useState('');
  const [escalationVariants, setEscalationVariants] = useState<Set<string>>(new Set());
  const [forceReview, setForceReview] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    setError(null);
    setSubject('');
    setNotes(buildIdTemplate(linkedItems));
    setEscalationVariants(new Set());
    setForceReview(false);
    // Only re-template when the dialog transitions to open with this specific set of items -
    // re-running on every linkedItems re-render would stomp on what the analyst is typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, linkedItems.map((i) => i.id).join(',')]);

  useEffect(() => {
    if (!open) {
      return;
    }

    // Mirror the dedicated subject field into the body's own "Subject" line, so the analyst isn't
    // typing the subject twice. Deliberately excludes `notes` from the deps (and uses the functional
    // setNotes form) so this only fires on a subject edit, not on every keystroke in the body itself;
    // if the analyst has deleted the placeholder line, this is a no-op rather than re-inserting it.
    setNotes((previousNotes) => withSyncedSubjectLine(previousNotes, subject));
  }, [open, subject]);

  const policyViolations = [...new Set(linkedItems.map((i) => i.policyViolation))];
  const {data: allVariants = []} = useEscalationVariants();
  const missingVariantVerticals = getPolicyViolationsMissingVariant(
    policyViolations,
    allVariants,
    escalationVariants
  );

  async function handleCreate() {
    setError(null);
    try {
      const trimmedSubject = subject.trim();
      await createP0Alert.mutateAsync({
        submission_ids: submissionIds,
        ...(trimmedSubject ? {subject: trimmedSubject} : {}),
        ...(isRichTextEmpty(notes) ? {} : {notes}),
        escalation_variants: [...escalationVariants],
        ...(forceReview ? {force_review: true} : {})
      });
      onOpenChange(false);
      showToast('P0 alert created.');
      onCreated?.();
    } catch (error_) {
      setError(getApiErrorMessage(error_, 'Failed to create P0 alert.'));
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen, details) => {
        // A stray click on the backdrop would discard the write-up; Cancel (or the X) closes it.
        if (details.reason === 'outside-press') {
          return;
        }

        onOpenChange(nextOpen);
      }}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MailWarning className="text-primary-ink size-4" />
            Create P0 Alert
          </DialogTitle>
          <DialogDescription>
            References {linkedItems.length} submission
            {linkedItems.length === 1 ? '' : 's'}
            {policyViolations.length > 1 ? ' across multiple policy violations' : ''}.{' '}
            {user?.skipP0Approval
              ? 'Your alerts skip approval — this will deliver immediately.'
              : 'It will be submitted for approval and delivered automatically once approved.'}
          </DialogDescription>
        </DialogHeader>

        {/* The body is the one part that scrolls between the fixed title and the pinned actions,
            so Submit stays reachable however long the form runs. */}
        <DialogBody>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-4">
              {/* flex-col so the viewport, a flex item, shrinks to the max height; a bare max-h on
                  the root leaves the viewport's 100% height unresolved and it grows to the list. */}
              <ScrollArea className="border-border flex max-h-40 flex-col rounded-md border">
                <div className="space-y-1.5 p-2">
                  {linkedItems.map((item) => (
                    <div key={item.id} className="flex items-center gap-2 text-xs">
                      <Badge variant="secondary">
                        {item.submissionType === 'Actor' ? ACTOR_LABEL : CONTENT_LABEL}
                      </Badge>
                      <Badge variant="outline">{item.policyViolation}</Badge>
                      <span className="truncate font-medium">{item.displayName}</span>
                    </div>
                  ))}
                </div>
              </ScrollArea>

              <Field>
                <FieldLabel htmlFor="p0-subject">
                  Subject line <span className="text-destructive-ink">*</span>
                </FieldLabel>
                <Input
                  id="p0-subject"
                  value={subject}
                  placeholder="Subject line used on the outbound email"
                  onChange={(event) => {
                    setSubject(event.target.value);
                  }}
                />
              </Field>

              <EscalationVariantPicker
                policyViolations={policyViolations}
                value={escalationVariants}
                onChange={setEscalationVariants}
              />

              <Field>
                <FieldLabel>Email body</FieldLabel>
                <RichTextArea
                  value={notes}
                  placeholder="Context for the reviewer and downstream recipients…"
                  onChange={setNotes}
                />
              </Field>

              {user?.skipP0Approval ? (
                <Field orientation="horizontal">
                  <Checkbox
                    id="p0-force-review"
                    checked={forceReview}
                    onCheckedChange={(checked) => {
                      setForceReview(checked);
                    }}
                  />
                  <FieldLabel htmlFor="p0-force-review" className="text-muted-foreground">
                    Send for review anyway (skip auto-delivery)
                  </FieldLabel>
                </Field>
              ) : null}

              {error ? (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              ) : null}
            </div>

            <EmailPreview subject={subject} bodyHtml={notes} />
          </div>
        </DialogBody>

        <DialogFooter sticky>
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              onOpenChange(false);
            }}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={
              createP0Alert.isPending || subject.trim() === '' || missingVariantVerticals.length > 0
            }
            onClick={() => void handleCreate()}>
            {createP0Alert.isPending ? <Spinner /> : <MailWarning />}
            {user?.skipP0Approval && !forceReview ? 'Create & Deliver' : 'Submit for Approval'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/// HTML-escapes a value interpolated into the template below - risk type/policy violation names and
/// external ids are analyst/admin-authored data, not free user input, but this still ends up in the
/// same contentEditable + dangerouslySetInnerHTML paths as the rest of the email body, so it's
/// escaped like anything else that reaches innerHTML rather than assumed safe by provenance.
function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

/// Matches the body's own `<p><strong>Subject</strong>: ...</p>` line, as produced by
/// `buildIdTemplate` below - used to keep that line's value mirroring the dedicated subject field.
const SUBJECT_LINE_PATTERN = /(<strong>Subject<\/strong>:)([^<]*)(<\/p>)/;

/// Replaces the value in the body's "Subject" line with the given subject, leaving the rest of the
/// (analyst-edited) body untouched. A no-op if the analyst has removed that line entirely, rather
/// than re-inserting it against their edit.
function withSyncedSubjectLine(notes: string, subject: string): string {
  if (!SUBJECT_LINE_PATTERN.test(notes)) {
    return notes;
  }

  return notes.replace(
    SUBJECT_LINE_PATTERN,
    (_match, pre: string, _value: string, post: string) => `${pre} ${escapeHtml(subject)}${post}`
  );
}

/// Pre-fills the email body with the standard P0 template - risk type, a description the analyst
/// fills in, then the video/channel IDs already known from the linked submissions, grouped by type,
/// so the analyst doesn't have to type them out by hand.
function buildIdTemplate(linkedItems: P0LinkedItem[]): string {
  // De-duplicated: one item can be linked once per policy violation in a multi-vertical P0, and the
  // same id listed twice reads as two separate videos.
  const idsOfType = (type: P0LinkedItem['submissionType']) =>
    [...new Set(linkedItems.filter((i) => i.submissionType === type).map((i) => i.externalId))].map(
      escapeHtml
    );

  const channelIds = idsOfType('Actor');
  const videoIds = idsOfType('Content');
  const riskType = escapeHtml([...new Set(linkedItems.map((i) => i.policyViolation))].join(', '));

  const idList = (ids: string[]) => `<ul>${ids.map((id) => `<li>${id}</li>`).join('')}</ul>`;

  // Falcon's alert names each profile by username as well as id, and carries no sign-off.
  const isFalcon = PLATFORM_ID === 'pinterest';
  const handles = new Map(linkedItems.map((i) => [i.externalId, i.handle]));
  const channelEntries = isFalcon
    ? [
        ...new Set(linkedItems.filter((i) => i.submissionType === 'Actor').map((i) => i.externalId))
      ].map((id) => {
        const handle = handles.get(id);
        return handle ? `${escapeHtml(handle)} (${escapeHtml(id)})` : escapeHtml(id);
      })
    : channelIds;

  return [
    '<p><strong>P0 Alert:</strong></p>',
    `<p><strong>Risk Type</strong>: ${riskType}</p>`,
    '<p><strong>Subject</strong>: </p>',
    '<p><strong>Description</strong></p>',
    '<ul><li><br></li></ul>',
    // Only include a section for the type(s) actually present - a content-only P0 gets no empty
    // actor-ID section, and vice versa.
    ...(channelIds.length > 0
      ? [
          `<p><strong>${ACTOR_LABEL}${isFalcon ? ' username / ID' : ' ID'}</strong></p>`,
          idList(channelEntries)
        ]
      : []),
    ...(videoIds.length > 0
      ? [`<p><strong>${CONTENT_LABEL} ID</strong></p>`, idList(videoIds)]
      : []),
    // Sign-off lives here (editable by the analyst) rather than hardcoded server-side.
    ...(isFalcon ? [] : ['<p><br></p>', '<p>Kind regards,<br>RYE</p>'])
  ].join('');
}
